import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SeoHead from '../SeoHead';
import { colors } from '../../styles/designSystem';

const CONTENT = {
  'agent-vocal-ia': {
    fr: ['Agent vocal IA pour les appels professionnels', 'Découvrez comment un agent vocal IA peut accueillir les appelants, recueillir leur demande et transmettre les informations utiles.'],
    en: ['AI voice agent for business calls', 'Learn how an AI voice agent can welcome callers, collect their request and pass on useful information.'],
    de: ['KI-Sprachagent für geschäftliche Anrufe', 'Erfahren Sie, wie ein KI-Sprachagent Anrufende begrüßen, Anliegen aufnehmen und nützliche Informationen weitergeben kann.'],
  },
  'standard-telephonique-ia': {
    fr: ['Standard téléphonique IA pour PME', 'Un standard téléphonique IA peut aider votre équipe à traiter les appels entrants et à orienter chaque demande.'],
    en: ['AI phone answering for small businesses', 'AI phone answering can help your team handle incoming calls and direct each request.'],
    de: ['KI-Telefonzentrale für KMU', 'Eine KI-Telefonzentrale kann Ihr Team bei eingehenden Anrufen und der Weiterleitung von Anliegen unterstützen.'],
  },
  'prise-rendez-vous-ia': {
    fr: ['Prise de rendez-vous par IA', 'Aidez les appelants à formuler leur besoin et à organiser un rendez-vous selon les règles définies par votre entreprise.'],
    en: ['AI appointment scheduling by phone', 'Help callers explain their needs and arrange an appointment according to your business rules.'],
    de: ['KI-gestützte Terminvereinbarung', 'Helfen Sie Anrufenden, ihr Anliegen zu erklären und einen Termin nach Ihren Geschäftsregeln zu vereinbaren.'],
  },
  'assistant-ia-artisans': {
    fr: ['Assistant IA pour artisans', 'Centralisez les demandes téléphoniques pendant les interventions et facilitez le suivi des prospects.'],
    en: ['AI call assistant for tradespeople', 'Centralize phone requests while you are on a job and make prospect follow-up easier.'],
    de: ['KI-Anrufassistent für Handwerksbetriebe', 'Bündeln Sie telefonische Anfragen während Einsätzen und erleichtern Sie die Nachverfolgung.'],
  },
  'assistant-ia-avocats': {
    fr: ['Assistant IA pour cabinets d’avocats', 'Recueillez les premières informations d’un appel sans présenter l’assistant comme un conseil juridique.'],
    en: ['AI call assistant for law firms', 'Collect initial call information without presenting the assistant as legal advice.'],
    de: ['KI-Anrufassistent für Kanzleien', 'Erfassen Sie erste Informationen zu einem Anruf, ohne den Assistenten als Rechtsberatung darzustellen.'],
  },
  'assistant-ia-comptables': {
    fr: ['Assistant IA pour cabinets comptables', 'Organisez les demandes entrantes et préparez leur transmission à la bonne personne du cabinet.'],
    en: ['AI call assistant for accounting firms', 'Organize incoming requests and prepare them for the right person at the firm.'],
    de: ['KI-Anrufassistent für Steuerkanzleien', 'Organisieren Sie eingehende Anliegen und bereiten Sie die Weitergabe an die richtige Person vor.'],
  },
  tarifs: {
    fr: ['Tarifs Sonitrix', 'Les besoins varient selon le volume d’appels et le parcours attendu. Contactez Sonitrix pour étudier une configuration adaptée.'],
    en: ['Sonitrix pricing', 'Needs vary with call volume and the expected caller journey. Contact Sonitrix to discuss a suitable setup.'],
    de: ['Sonitrix Preise', 'Der Bedarf hängt vom Anrufvolumen und gewünschten Ablauf ab. Kontaktieren Sie Sonitrix für eine passende Konfiguration.'],
  },
  integrations: {
    fr: ['Intégrations Sonitrix', 'Les intégrations dépendent de votre environnement et de la configuration retenue. Vérifiez la compatibilité avec notre équipe.'],
    en: ['Sonitrix integrations', 'Integrations depend on your environment and chosen setup. Confirm compatibility with our team.'],
    de: ['Sonitrix Integrationen', 'Integrationen hängen von Ihrer Umgebung und Konfiguration ab. Prüfen Sie die Kompatibilität mit unserem Team.'],
  },
  'securite-rgpd': {
    fr: ['Sécurité et RGPD', 'Comprenez les points à valider pour intégrer un assistant vocal à votre organisation et à vos obligations de protection des données.'],
    en: ['Security and GDPR', 'Review the points to validate when adding a voice assistant to your organization and data-protection obligations.'],
    de: ['Sicherheit und DSGVO', 'Prüfen Sie die Punkte für die Einbindung eines Sprachassistenten in Ihre Organisation und Datenschutzpflichten.'],
  },
  'etudes-de-cas': {
    fr: ['Études de cas Sonitrix', 'Explorez les scénarios d’usage présentés par Sonitrix. Les résultats dépendent du contexte et de la configuration de chaque entreprise.'],
    en: ['Sonitrix case studies', 'Explore usage scenarios presented by Sonitrix. Results depend on each company’s context and configuration.'],
    de: ['Sonitrix Fallstudien', 'Entdecken Sie Anwendungsszenarien von Sonitrix. Ergebnisse hängen vom Kontext und der Konfiguration jedes Unternehmens ab.'],
  },
  guides: {
    fr: ['Guides sur les assistants vocaux IA', 'Des repères pour évaluer, préparer et déployer un assistant vocal adapté à la gestion de vos appels.'],
    en: ['AI voice assistant guides', 'Practical guidance for evaluating, preparing and deploying a voice assistant for your call management.'],
    de: ['Leitfäden für KI-Telefonassistenten', 'Praktische Hinweise zur Auswahl, Vorbereitung und Einführung eines Sprachassistenten für Ihre Anrufe.'],
  },
};

const EditorialPage = ({ slug }) => {
  const { lang = 'en' } = useParams();
  const currentLang = ['fr', 'en', 'de'].includes(lang) ? lang : 'en';
  const [title, description] = CONTENT[slug][currentLang];
  const homeLabel = { fr: 'Retour à l’accueil', en: 'Back to home', de: 'Zur Startseite' }[currentLang];
  const points = {
    fr: ['Accueil cohérent des appelants', 'Collecte structurée de la demande', 'Transmission à votre équipe'],
    en: ['Consistent caller welcome', 'Structured request collection', 'Handoff to your team'],
    de: ['Einheitliche Begrüßung', 'Strukturierte Anliegenaufnahme', 'Übergabe an Ihr Team'],
  }[currentLang];

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-gray-100 min-h-[70vh]">
      <SeoHead lang={currentLang} path={slug} title={`${title} | Sonitrix`} description={description} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className={`${colors.text.heading} ${colors.primary.text} mb-6 text-4xl lg:text-5xl`}>{title}</h1>
        <p className={`${colors.text.body} text-lg mb-8 max-w-3xl`}>{description}</p>
        <div className={`${colors.container.base} p-6 max-w-3xl`}>
          <ul className="space-y-4">
            {points.map((point) => <li key={point} className="flex items-center"><CheckCircle className="w-5 h-5 text-violet-600 mr-3" />{point}</li>)}
          </ul>
        </div>
        <Link to={`/${currentLang}/`} className="inline-flex items-center mt-8 text-violet-600 hover:text-violet-700 font-medium">
          {homeLabel}<ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </div>
    </main>
  );
};

export { CONTENT };
export default EditorialPage;
